// 底部 Tab 栏组件
// src/components/tab-bar/index.tsx

'use client';

import { useEffect, useRef } from 'react';

import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';

import cls from 'classnames';

import DiscoverActiveIcon from '@/assets/svg/discover-active.svg';
import DiscoverIcon from '@/assets/svg/discover.svg';
import MyListActiveIcon from '@/assets/svg/my-list-active.svg';
import MyListIcon from '@/assets/svg/my-list.svg';
import ProfileActiveIcon from '@/assets/svg/profile-active.svg';
import ProfileIcon from '@/assets/svg/profile.svg';
import AppLink from '@/components/app-link';
import { ROUTER } from '@/constants';
import { useDebounce } from '@/hooks';
import { ETrackerEvents, trackCustom } from '@/utils/tracker';
import {
    consumeEnterTabType,
    consumeLeftReaderToTab,
    consumeReaderSession,
    markEnteredReader,
    type IReaderSession,
    type TTabEnterType,
} from '@/utils/tracker/tab-nav-session';

/** Tab 埋点上报名 与 BI 口径对齐，独立于展示 key */
type TTabTrackName = 'my_list' | 'discover' | 'profile';

/** TabBar 命名空间下的 i18n key 联合 */
type TTabLabelKey = 'tab_my_list' | 'tab_discover' | 'tab_profile';

/** Tab 项配置 */
interface ITabItem {
    /** React 列表 key 也用于在 DOM/调试中识别 tab */
    key: string;
    /** 埋点上报名 与 BI 口径绑定，重命名展示 key 时不会污染报表 */
    trackName: TTabTrackName;
    /** i18n key，由 useTranslations('TabBar') 解析为展示文案 */
    labelKey: TTabLabelKey;
    /** 路由路径，与 usePathname 比对以高亮当前项 */
    path: string;
    /** 未激活态图标组件 */
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
    /** 激活态图标组件 */
    ActiveIcon: React.FC<React.SVGProps<SVGSVGElement>>;
}

/** Tab 项静态配置 文案在 render 时由 i18n 函数生成，避免组件内每次 render 重建数组 */
const TAB_ITEMS: ITabItem[] = [
    {
        key: 'My_list',
        trackName: 'my_list',
        labelKey: 'tab_my_list',
        path: ROUTER.MY_LIST,
        Icon: MyListIcon,
        ActiveIcon: MyListActiveIcon,
    },
    {
        key: 'Discover',
        trackName: 'discover',
        labelKey: 'tab_discover',
        path: ROUTER.DISCOVER,
        Icon: DiscoverIcon,
        ActiveIcon: DiscoverActiveIcon,
    },
    {
        key: 'Profile',
        trackName: 'profile',
        labelKey: 'tab_profile',
        path: ROUTER.PROFILE,
        Icon: ProfileIcon,
        ActiveIcon: ProfileActiveIcon,
    },
];

/** Tab 点击埋点防抖间隔，避免快速连点重复上报 */
const TAB_CLICK_DEBOUNCE_MS = 700;

/** 上报 Tab 点击埋点 */
function reportTabClick(tab: ITabItem): void {
    trackCustom(ETrackerEvents.TAB_CLICK, {
        tab_name: tab.trackName,
    });
}

/** 根据 pathname 获取对应 tab 埋点名；非 tab 页面返回 null */
function resolveTabNameByPath(path: string): TTabTrackName | null {
    const tab = TAB_ITEMS.find(item => item.path === path);
    return tab?.trackName ?? null;
}

interface ITabSession {
    tabName: TTabTrackName;
    enterAt: number;
    /** 来源：上一个 tab；从 reader 返回时为 'reader'；无来源（冷启动）为 '' */
    fromTab: TTabTrackName | 'reader' | '';
    /** 进入方式：本次页面会话首个 enter_tab 为 launch_app，其后均为 switch；stay_tab 沿用同一值保证配对一致 */
    enterType: TTabEnterType;
}

/** 上报进入 tab */
function reportEnterTab(session: ITabSession): void {
    trackCustom(ETrackerEvents.ENTER_TAB, {
        tab_name: session.tabName,
        enter_type: session.enterType,
        stay_time: 0,
        from_tab: session.fromTab || session.tabName || '',
    });
}

/** 上报离开 tab */
function reportStayTab(session: ITabSession): void {
    trackCustom(ETrackerEvents.STAY_TAB, {
        tab_name: session.tabName,
        enter_type: session.enterType,
        stay_time: Date.now() - session.enterAt,
        from_tab: session.fromTab,
    });
}

/** 上报进入 reader（tab 切换到阅读器时，由 TabBar 侧补发新模块的 enter_tab，并记录 reader 会话） */
function reportEnterReader(fromTab: TTabTrackName): void {
    markEnteredReader(fromTab);
    trackCustom(ETrackerEvents.ENTER_TAB, {
        tab_name: 'reader',
        enter_type: 'switch',
        stay_time: 0,
        from_tab: fromTab,
    });
}

/** 上报离开 reader（停留时长取 tab → reader 时记录的 reader 会话） */
function reportStayReader(session: IReaderSession): void {
    trackCustom(ETrackerEvents.STAY_TAB, {
        tab_name: 'reader',
        enter_type: session.fromTab ? 'switch' : 'launch_app',
        stay_time: Date.now() - session.enterAt,
        from_tab: session.fromTab,
    });
}

/** 判断路径是否为 reader 阅读器页 */
function isReaderPath(path: string): boolean {
    return path.startsWith(ROUTER.READER);
}

/**
 * 底部 Tab 栏：固定在视口底部，供 (tabs) 布局使用。
 * 高度含 safe-area-inset-bottom，与 TabsLayout 的 pb 占位保持一致。
 */
export default function TabBar() {
    // i18n 文案
    const t = useTranslations('TabBar');

    // 当前路由路径 用于匹配高亮当前 tab
    const pathname = usePathname();

    // tab 点击埋点（防抖）leading：首次点击立即上报；trailing：防抖窗口内不再补发
    const handleTabClick = useDebounce(reportTabClick, TAB_CLICK_DEBOUNCE_MS, {
        leading: true,
        trailing: false,
    });

    // 当前 tab 会话：记录进入时间、来源 tab，用于 enter_tab / stay_tab 计算停留时长
    const sessionRef = useRef<ITabSession | null>(null);

    // pathname 变化时：上报离开旧 tab + 进入新 tab；离开 tabs 页面时上报离开
    useEffect(() => {
        const currentTab = resolveTabNameByPath(pathname);
        const prevSession = sessionRef.current;

        if (currentTab) {
            // 切换 tab：先上报离开旧 tab
            if (prevSession && prevSession.tabName !== currentTab) {
                reportStayTab(prevSession);
            }
            // 组件重挂载（如从 reader 返回）时会话丢失，消费 reader 侧标记还原来源为 reader
            const fromReader = !prevSession && consumeLeftReaderToTab();
            // 从 reader 返回：补报 reader 的 stay_tab（停留时长取 tab → reader 时记录的会话）
            if (fromReader) {
                const readerSession = consumeReaderSession();
                if (readerSession) {
                    reportStayReader(readerSession);
                }
            }
            const newSession: ITabSession = {
                tabName: currentTab,
                enterAt: Date.now(),
                fromTab: fromReader ? 'reader' : (prevSession?.tabName ?? ''),
                // 首个 enter_tab（含冷启动直接进 reader 再返回 tab 的场景）为 launch_app，其后均为 switch
                enterType: consumeEnterTabType(),
            };
            sessionRef.current = newSession;
            reportEnterTab(newSession);
        } else if (prevSession) {
            // 离开了 tabs 页面：仅去向 reader 时上报离开旧 tab + 进入 reader（埋点仅覆盖 reader 与三个 tab）
            if (isReaderPath(pathname)) {
                reportStayTab(prevSession);
                reportEnterReader(prevSession.tabName);
            }
            sessionRef.current = null;
        }
    }, [pathname]);

    // 组件卸载时：仅 tab → reader（(tabs) 布局整体卸载）补报离开旧 tab + 进入 reader；
    // 卸载时 usePathname 已不可用，通过 window.location 判断去向
    useEffect(() => {
        return () => {
            const session = sessionRef.current;
            sessionRef.current = null;
            if (session && isReaderPath(window.location.pathname)) {
                reportStayTab(session);
                reportEnterReader(session.tabName);
            }
        };
    }, []);

    return (
        // 固定吸底容器 高度含 safe-area-inset-bottom 适配全面屏
        <div className='fixed inset-x-0 bottom-0 z-[40] flex h-[calc(52px+env(safe-area-inset-bottom,0px))] flex-col items-center justify-start border-t border-[#f0f0f0] bg-white'>
            {/* tab 行：固定 375px 宽，等分排列 */}
            <div className='flex h-[52px] w-[375px] justify-center gap-[2px]'>
                {TAB_ITEMS.map(tab => {
                    const isActive = pathname === tab.path;
                    const IconComp = isActive ? tab.ActiveIcon : tab.Icon;
                    return (
                        // 单个 tab 项 使用 AppLink prefetch 提升切换体验，并透传染色参数 / 按需释放活动弹窗
                        <AppLink
                            key={tab.key}
                            href={tab.path}
                            prefetch
                            className='flex flex-1 cursor-pointer flex-col items-center justify-center gap-[2px] text-inherit no-underline'
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => handleTabClick(tab)}
                        >
                            {/* 图标 装饰性 旁有文案承担可读名称 故 aria-hidden 避免屏幕阅读器重复读 */}
                            <div className='size-[24px]'>
                                <IconComp aria-hidden />
                            </div>

                            {/* 文案 激活态紫色加粗 / 未激活灰色 */}
                            <span
                                className={cls('text-[10px] leading-[14px]', {
                                    'font-semibold text-[#7c3aed]': isActive,
                                    'text-[#999]': !isActive,
                                })}
                            >
                                {t(tab.labelKey)}
                            </span>
                        </AppLink>
                    );
                })}
            </div>
        </div>
    );
}
